import { shallowRef, computed, watch } from 'vue';
import { defineStore } from 'pinia';
import { useRoute } from 'vue-router';

const STORAGE_KEY = 'sidebar-collapsed';

/**
 * `collapsed` carries two meanings, because sidebar.vue switches on it with Tailwind classes
 * alone: at or above `md` it is the persisted desktop preference (icon rail vs. 220px panel),
 * below `md` it is whether the transient drawer overlay is showing. Anything that collapses the
 * sidebar for a desktop layout reason therefore also hides the mobile drawer.
 *
 * Tailwind's `md` is 48rem, so matchMedia would track a non-default root font size exactly, but
 * jsdom implements no matchMedia and the pixel check is what the tests can drive.
 */
const MOBILE_MAX_WIDTH = 767;

const isMobileViewport = () => window.innerWidth <= MOBILE_MAX_WIDTH;

const sidebarShouldBeCollapsed = (routeName) => {
	return ![
		'resumes',
		'cover-letters.index',
		'home',
		'settings',
		'profile',
		'services-tracker',
		'interview-ai',
		'admin-view',
		'admin-view-subgroups',
		'admin-view-subgroup-table'
	].some((e) => e === routeName);
};

const FORCE_COLLAPSED_ROUTES = [
	'resumes.edit'
];

export const useSidebarStore = defineStore('sidebar', () => {
	const collapsed = shallowRef(false);
	const route = useRoute();

	const userPreference = shallowRef(
		localStorage.getItem(STORAGE_KEY) !== null
			? localStorage.getItem(STORAGE_KEY) === 'true'
			: null
	);

	const resolveCollapsedState = (routeName) => {
		if (FORCE_COLLAPSED_ROUTES.includes(routeName)) { return true; }
		if (userPreference.value !== null) { return userPreference.value; }
		return sidebarShouldBeCollapsed(routeName);
	};

	const sidebarCollapseHandler = () => {
		collapsed.value = !collapsed.value;

		// EN-974: on mobile this toggles a transient drawer, not the desktop layout, so it must
		// not be recorded as a preference — one hamburger tap would otherwise pin the user's
		// desktop sidebar open on every device from then on.
		if (isMobileViewport()) { return; }

		userPreference.value = collapsed.value;
		localStorage.setItem(STORAGE_KEY, collapsed.value);
	};

	// EN-896: for callers that need to collapse the sidebar temporarily — the welcome tour
	// spotlights a menu item — without overwriting the preference the user actually chose.
	const setCollapsedWithoutPersisting = (value) => {
		collapsed.value = value;
	};

	// EN-974: the mobile drawer never survives a navigation; the desktop panel always does.
	const syncCollapsedForRoute = () => {
		collapsed.value = isMobileViewport() ? true : resolveCollapsedState(route.name);
	};

	// EN-974: every sidebar interaction except the submenu chevron dismisses the mobile drawer.
	// The route watcher below covers navigations; this covers what it cannot see — taps on the
	// route the user is already on, and actions that open a modal instead of navigating.
	const collapseOnMobile = () => {
		if (isMobileViewport()) { collapsed.value = true; }
	};

	let wasMobile = isMobileViewport();

	// Only react to a real breakpoint crossing. iOS Safari fires `resize` whenever the address
	// bar hides, which changes innerHeight but not innerWidth, and re-resolving there would shut
	// the drawer out from under a user who is scrolling it.
	const handleViewportChange = () => {
		const nowMobile = isMobileViewport();

		if (nowMobile === wasMobile) { return; }

		wasMobile = nowMobile;
		syncCollapsedForRoute();
	};

	const sidebarWidth = computed(() => collapsed.value ? '70px' : '220px');

	watch(() => route.name, syncCollapsedForRoute, { immediate: true });

	return {
		collapsed,
		sidebarWidth,

		collapseOnMobile,
		handleViewportChange,
		sidebarCollapseHandler,
		setCollapsedWithoutPersisting
	};
});
